import {
  Box,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import MobileBottomDrawer from "screens/patients_v2/mobile/Components/MobileBottomDrawer";
import ModeEditOutlinedIcon from "@mui/icons-material/ModeEditOutlined";
import PatientUpdateDrawer from "screens/patients/patientUpdateDrawer/PatientEditDrawer";
import { useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { formatDate } from "config/helpers/date_helper";
import { CREATE_AUDIT_EVENT, UPDATE_PATIENT } from "adapters/ehr.service";
import dayjs from "dayjs";
import { patientObject } from "redux/actions/PatientActions";
import { formatToE164, FormError, genderOptionList, isCountryCode } from "config/helpers/common_helper";
import UIDatePicker from "components/datepicker/UIDatePicker";
import UIButton from "components/ui/form/UIButton";
import { parseName } from "humanparser";
import useAlert from "components/ui/alert/Alert";
import UITextField from "components/ui/textField/UITextField";
import { isValidPhoneNumber } from "libphonenumber-js";
import UIPhoneNumber from "components/ui/textField/UIPhoneNumber";
import UISelect from "components/ui/select/UISelect";
import { isEqual } from "lodash";

const PatientUpdateMobileComponent = () => {
  const patientObj = useSelector((state) => state?.patientObj?.patientObj);

  const [closePopUp, setClosePopUp] = useState(false);

  const reduxDispatch = useDispatch();
  const { i18n } = useTranslation();

  const { showAlert, CustomAlert } = useAlert();

  const telecom = patientObj?.telecom || [];

  const email = telecom.find((t) => t.system === "email")?.value || "";
  const phone = telecom.find((t) => t.system === "phone")?.value || "+1";

  const formInitialValues = {
    patientName: patientObj?.name?.[0]?.text || "",
    dob: patientObj?.birthDate ? dayjs(patientObj?.birthDate) : null,
    gender: patientObj?.gender || "",
    patientPhone: phone,
    patientEmail: email,
  };
  const theme = useSelector((state) => state?.app.theme);

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    watch,
    reset,
  } = useForm({
    defaultValues: formInitialValues,
  });

  useEffect(() => {
    if (patientObj) {
      const phone = patientObj.telecom?.find(t => t.system === "phone")?.value || "+1";
      const email = patientObj.telecom?.find(t => t.system === "email")?.value || "";

      reset({
        patientName: patientObj?.name?.[0]?.text || "",
        dob: patientObj?.birthDate ? dayjs(patientObj?.birthDate) : null,
        gender: patientObj?.gender || "",
        patientPhone: phone,
        patientEmail: email,
      });
    }
  }, [patientObj, reset]);

  const PractitionerID = useSelector(
    (state) => state?.auth?.practitionerData?.id
  );
  const formCurrentValues = watch();

  const hasFormValuesChanged = () => {
    const initialValuesString = JSON.stringify(formInitialValues);
    const currentValuesString = JSON.stringify(formCurrentValues);

    // return initialValuesString !== currentValuesString;
    return !isEqual(formInitialValues, formCurrentValues)
  };

  const [isLoading, setIsLoading] = useState({ saveBtn: false });
  const isTab = useMediaQuery(
    (theme) => theme.breakpoints.up("sm") && theme.breakpoints.down("lg")
  );

  const handleOnSavePatient = async (data) => {
    if (!hasFormValuesChanged()) {
      // Display toast notification indicating no changes were made
      toast.warning(`${i18n.t("Patient_Screen.NO_CHANGES_TOAST")}`, {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 4000,
      });
      return; // Exit early if no changes were detected
    }
    setIsLoading(true);

    // Create a deep copy of props.patientObj to avoid mutating the original state
    let payload = JSON.parse(JSON.stringify(patientObj));

    if (Object.hasOwn(payload, "encounterReset")) {
      delete payload.encounterReset;
    }

    const attrs = parseName(data?.patientName);
    const { salutation, firstName, middleName, lastName, suffix } = attrs;

    const givenNames = middleName ? [firstName, middleName] : [firstName];

    const telecom = [];

    if (data?.patientEmail) {
      telecom.push({
        system: "email",
        value: data.patientEmail.trim(),
        use: "home",
      });
    }

    if (data?.patientPhone && formatToE164(data.patientPhone.trim())) {
      telecom.push({
        system: "phone",
        // value: data.patientPhone.trim(),
        value: formatToE164(data.patientPhone.trim()),
        use: "mobile",
      });
    }

    if (telecom?.length > 0) {
      payload.telecom = telecom;
    } else {
      delete payload.telecom;
    }

    const nameList = [
      {
        use: "official",
        family: lastName,
        given: givenNames,
        prefix: salutation ? [salutation.trim()] : [],
        suffix: suffix ? [suffix.trim()] : [],
        text: data?.patientName?.trim(),
      },
    ];

    if (data?.patientName) {
      payload.name = nameList;
    }

    if (data?.dob) {
      payload.birthDate = formatDate(data?.dob, "-", "YYYY-MM-DD");
    } else {
      delete payload.birthDate;
    }

    if (data?.gender) {
      payload.gender = data?.gender;
    }

    delete payload.clinicalImpressionReset;

    try {
      setIsLoading({ saveBtn: true });
      const response = await UPDATE_PATIENT({
        resourceType: "Patient",
        ...payload,
      });
      CREATE_AUDIT_EVENT({
        practitionerID: `Practitioner/${PractitionerID}`,
        patientName: payload.name[0].text,
        type: { display: "Update", code: "110121" },

        reason: JSON.stringify({
          msg_code: "PATIENT_MODIFIED",
          data: payload?.name?.[0]?.text,
        }),
      });

      toast.success(i18n.t("common.PATIENT_UPDATED_SUCCESSFULLY"));
      reduxDispatch(patientObject(response?.data));
    } catch (error) {
      console.error("Failed to update patient", error);
      toast.error("Failed to update patient");
    } finally {
      reset({ ...data });
      setIsLoading(false);
      setClosePopUp(true);
    }
  };

  const isNotEmpty = (value) => value?.trim() !== "";

  const handleClose = () => {
    if (isDirty) {
      showAlert(
        i18n.t("common.UNSAVED_CHANGES"),
        i18n.t("common.UNSAVED_CHANGES_MESSAGE"),
        () => {
          reset(); // Reset form values
          setClosePopUp(true);
        }
      );
    }
  };

  return (
    <MobileBottomDrawer
      reset={reset}
      isDirty={isDirty}
      closePopUp={closePopUp}
      setClosePopUp={setClosePopUp}
      handleClose={handleClose}
      buttonComponent={
        <IconButton disableRipple sx={{ background: theme === "dark" ? "#272652" : "#fff", color: theme === "dark" ? "#fff" : "#272652" }}>
          <ModeEditOutlinedIcon />
        </IconButton>
      }
      component={
        <>
          <CustomAlert />
          <form
            // sx={{
            //   display: "flex",
            //   flexDirection: "column",
            //   gap: "16px",
            // }}
            className='d-flex flex-column gap-3 text-start'
            onSubmit={handleSubmit(handleOnSavePatient)}
          >
            <Typography className="text-start" variant="h5">{i18n.t("common.EDIT")}</Typography>
            <Grid
              container
              spacing={1}
            // className="d-flex gap-2 align-items-start"
            >
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="patientName"
                  rules={{
                    required: i18n.t(
                      "patient2.PATIENT_FIRST_NAME_LAST_NAME_IS_REQUIRED"
                    ),
                    validate: {
                      isNotEmpty: (value) =>
                        isNotEmpty(value) ||
                        i18n.t(
                          "patient2.PATIENT_FIRST_NAME_LAST_NAME_IS_REQUIRED"
                        ),
                      isAlphabetical: (value) =>
                        /^(?=\p{L})[\p{L}0-9., ]*$/u.test(value) ||
                        i18n.t("error_messages.INVALID_NAME"), // Add an error message for invalid name
                    },
                  }}
                  render={({ field }) => (
                    <UITextField
                      {...field}
                      autoFocus={!isTab}
                      inputRef={field?.ref}
                      // autoFocus
                      autoComplete="off"
                      placeholder={i18n.t(
                        "Patient_Screen.PATIENT_FIRST_NAME_LAST_NAME"
                      )}
                      fieldLabel={i18n.t(
                        "Patient_Screen.PATIENT_FIRST_NAME_LAST_NAME"
                      )}
                      required
                      variant="outlined"
                      fullWidth
                      size="small"
                      error={errors?.patientName?.message}
                      sx={{
                        "& .MuiInputBase-input::placeholder": {
                          opacity: 0.8,
                          fontWeight: 500,
                          color: theme === "dark" ? "#aaa" : "#aaa",
                        },
                        // // Style for the asterisk
                        // "& .MuiInputBase-root": {
                        //   "&::after": {
                        //     content: `'${field?.value ? "" : "*"}'`,
                        //     color: "red", // Set asterisk color to red
                        //     position: "absolute",
                        //     left: "5px",
                        //   },
                        // },
                        fontSize: "1.2vh",
                      }}
                    />
                  )}
                />
                <FormError
                  sx={{ fontSize: "1.2vh" }}
                  error={errors?.patientName?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="gender"
                  rules={{
                    required: i18n.t("error_messages.GENDER_REQUIRED"),
                    validate: (value) =>
                      isNotEmpty(value) ||
                      i18n.t("error_messages.GENDER_REQUIRED"),
                  }}
                  render={({ field }) => (
                    <>
                      <UISelect
                        required
                        label={i18n.t("Patient_Screen.GENDER")}
                        // relative={true}
                        fullWidth
                        adaptiveScreen
                        fullwidth
                        selectedValues={genderOptionList
                          ?.map((item) => {
                            return {
                              ...item,
                              label: i18n.t(item?.label),
                            };
                          })
                          ?.find((f) => f?.value === field?.value)}
                        onChange={(valueObj) =>
                          field?.onChange(valueObj?.value)
                        }
                        options={genderOptionList?.map((item) => {
                          return {
                            ...item,
                            label: i18n.t(item?.label),
                          };
                        })}
                        error={errors?.gender}
                        placeholder={
                          <div
                            style={{
                              textAlign: "left",
                              fontSize: "0.7rem",
                            }}
                            className="fs-7"
                          >
                            {/* <span style={{ color: "red" }}>*</span> */}
                            {i18n.t("Patient_Screen.GENDER")}
                          </div>
                        }
                        // onKeyDown={handleKeyDown}
                        ref={field?.ref}
                      // menuPosition={"fixed"}
                      />
                    </>
                  )}
                />
                {errors.gender && (
                  <FormError
                    sx={{ fontSize: "1.2vh" }}
                    error={errors.gender.message}
                  />
                )}
              </Grid>
              <Grid item sm={12} xs={12}>
                <Controller
                  control={control}
                  name="dob"
                  rules={{
                    required: i18n.t("error_messages.DOB_REQUIRED"),
                    validate: (value) => {
                      const inputDate = dayjs(value).startOf("day");

                      if (!inputDate.isValid()) {
                        return i18n.t("patient2.INVALID_DATE_FORMAT");
                      }

                      const maxDate = dayjs().startOf("day");
                      if (inputDate.isAfter(maxDate)) {
                        return i18n.t("patient2.DOB_CANNOT_BE_FUTURE_DATE");
                      }

                      const minDate = dayjs("1900-01-01").startOf("day");
                      if (inputDate.isBefore(minDate)) {
                        return i18n.t("patient2.DOB_CANNOT_BE_BEFORE_1900");
                      }

                      return true;
                    }

                  }}
                  render={({ field }) => (
                    <UIDatePicker
                      {...field}
                      fieldLabel={i18n.t("Patient_Screen.DATE_OF_BIRTH")}
                      required
                      maxDate={dayjs()}
                      minDate={dayjs('1900-01-01')}
                      placeholder={i18n.t("Patient_Screen.DATE_OF_BIRTH")}
                      size={"small"}
                      showStartAdornment={false}
                      sx={{
                        // "& .MuiInputBase-root": {
                        //   "&::after": {
                        //     content: `'${field?.value ? "" : "*"}'`,
                        //     color: "red", // Set asterisk color to red
                        //     position: "absolute",
                        //     left: "5px",
                        //   },
                        // },
                      }}
                      inputProps={{
                        sx: {
                          fontSize: "1.2vh",
                        }
                      }}
                      inputRef={field?.ref}
                      error={errors.dob}
                      fullWidth
                      placement={"auto"}
                    />
                  )}
                />
                <FormError
                  sx={{ fontSize: "1.2vh" }}
                  error={errors?.dob?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="patientPhone"
                  defaultValue={'+1'}
                  rules={{
                    validate: (value) => {
                      if (isCountryCode(value) || value === '+' || !value) {
                        return true
                      }
                      return !!formatToE164(value) || i18n.t("error_messages.INVALID_PHONE")
                    }
                  }}
                  render={({ field }) => (
                    <UIPhoneNumber
                      {...field}
                      inputProps={{
                        ref: field?.ref,
                      }}
                      fieldLabel={i18n.t("ehr.personalInfo.CELL_PHONE")}
                      defaultCountry="us"
                      fullWidth
                      value={field.value}
                      onChange={(value) => {
                        let cleaned = value.replace(/[()\s-]/g, '');
                        field.onChange(cleaned)
                      }}
                      onFocus={() => { }}
                      error={!!errors?.patientPhone}
                      sx={{
                        ".MuiInputLabel-root": {
                          zIndex: 0, // Make sure the label stays above the input
                        },
                        fontSize: "1.2vh",
                      }}
                    />
                  )}
                />

                <FormError
                  sx={{ fontSize: "1.2vh" }}
                  error={errors?.patientPhone?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="patientEmail"
                  rules={{
                    maxLength: {
                      value: 100,
                      message: i18n.t("error_messages.EMAIL_MAX_LENGTH"),
                    },
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                      message: i18n.t("error_messages.INVALID_EMAIL"),
                    },
                  }}
                  render={({ field }) => (
                    <UITextField
                      fieldLabel={
                        i18n.t("dashboard2.EMAIL")
                      }
                      {...field}
                      autoComplete="off"
                      placeholder={i18n.t("dashboard2.EMAIL")}
                      variant="outlined"
                      fullWidth
                      size="small"
                      error={!!errors?.patientEmail}
                      // helperText={errors?.patientEmail?.message}
                      sx={{
                        "& .MuiInputBase-input::placeholder": {
                          // color: "black",
                          fontSize: "0.7rem",
                          opacity: 1,
                          fontWeight: 500,
                          color: theme === "dark" ? "#aaa" : "#aaa", // Change this to the desired color
                        },
                        fontSize: "1.2vh",
                      }}
                      inputRef={field?.ref}
                    />
                  )}
                />
                <FormError
                  sx={{ fontSize: "1.2vh" }}
                  error={errors?.patientEmail?.message}
                />
              </Grid>
            </Grid>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                gap: "10px",
              }}
            >
              <UIButton
                fullWidth
                callback={handleSubmit(handleOnSavePatient)}
                variant="contained"
                text={i18n.t("common.UPDATE")}
                loading={isLoading?.saveBtn}
                className="rounded-5"
                type='submit'
              // loadingIndicator={"Creating patient..."}
              />

              <UIButton
                fullWidth
                callback={() => {
                  reset(); // Reset form values
                  setClosePopUp(true);
                }}
                variant="outlined"
                text={i18n.t("Patient_Screen.CANCEL")}
                className="rounded-5"
              // loading={isLoading}
              // loadingIndicator={"Creating patient..."}
              />
            </Box>
          </form>
        </>
      }
    />
  );
};

export default PatientUpdateMobileComponent;
